import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { fetchApi, fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { alertSlice } from "src/store/alert/alert.slice";
import { selector } from "src/react-query/select";
import { is } from "src/util/is";
import {
  useSelectedCompany,
  useSelectedTeam,
  useTeamListRedux,
} from "./useSelectedTeam";
import { isSameDay } from "date-fns";
import { withinTimeBounds } from "src/utils/formatTime";
import { useState } from "react";
import Token from "src/util/Token";

const updateChargerList = (chargers) => {
  const updatedChargerList = chargers.reduce((updatedChargerList, charger) => {
    // Filter out deleted chargers
    if (charger.registration_status === 'DELETE') {
      return updatedChargerList;
    }
    
    let status = "Inactive";
    if (charger.status === "A") {
      status = "Active";
    }

    const updatedCharger = { ...charger, status };
    updatedChargerList = [...updatedChargerList, updatedCharger];
    return updatedChargerList;
  }, []);
  return updatedChargerList;
};

export const useList = (queryKey, enabled, customTeamId, params) => {
  const { teamId } = useSelectedTeam();

  // Use customTeamId if provided, otherwise fall back to teamId from context
  const effectiveTeamId = customTeamId || teamId;

  const commonOptions = {
    queryFn: fetchWithError,
    // Selector returns the full response so pagination metadata is preserved
    select: (response) => response,
  };

  const vehicleQuery = useQuery({
    ...commonOptions,
    queryKey: queryKeys[queryKey](effectiveTeamId, params),
    enabled: queryKey === "vehicles" && (enabled ?? !is.empty(effectiveTeamId)),
  });

  const defaultQuery = useQuery({
    ...commonOptions,
    queryKey: queryKeys[queryKey](effectiveTeamId, params),
    enabled: queryKey !== "vehicles" && (enabled ?? !is.empty(effectiveTeamId)),
  });

  const { data: response, isFetching, refetch } =
    queryKey === "vehicles" ? vehicleQuery : defaultQuery;

  // Extract records array and pagination metadata from the response.
  // Handles both paginated shape { data: [...], pagination: {...} }
  // and legacy shape where the response itself is the array.
  const records = Array.isArray(response?.data) ? response.data
                : Array.isArray(response)        ? response
                : undefined;
  const pagination = response?.pagination ?? null;

  if (queryKey === "chargers" && !is.empty(records)) {
    const updatedChargerList = updateChargerList(records);
    return { data: updatedChargerList, pagination, isFetching, refetch };
  }
  return { data: records, pagination, isFetching, refetch };
};

export const useGetHistory = (queryKey, id) => {
  return useQuery(queryKeys[queryKey](id), fetchWithError);
};

export const useGetDetail = (queryKey, { id, disabled = false } = {}) => {
  const { teamId } = useSelectedTeam();
  const enabledCriteria =
    queryKey === "availableTags"
      ? !is.empty(teamId)
      : !is.empty(teamId) && !is.empty(id);
  return useQuery(queryKeys[queryKey](teamId, id), fetchWithError, {
    enabled: enabledCriteria && !disabled,
    select: selector,
  });
};

// New hook specifically for availableVehicles
export const useAvailableVehicles = ({ id, disabled = false } = {}) => {
  const company = useSelectedCompany();
  
  return useQuery(
    queryKeys.availableVehicles(company.id, id),
    fetchWithError,
    {
      enabled: !disabled && !is.empty(company.id) && (id ? !is.empty(id) : true),
      select: selector,
    }
  );
};

export const useGetSettlementPercentage = (isCreateTeam) => {
  return useQuery(queryKeys["settlementPercentage"], fetchWithError, {
    enabled: !!isCreateTeam,
    select: selector,
  });
};

export const useGetPaymentDetailsForPriceGroup = (
  isCreatePriceGroup,
  teamId
) => {
  return useQuery(
    queryKeys["paymentDetailsForPriceGroup"](teamId),
    fetchWithError,
    {
      enabled: !!isCreatePriceGroup,
      select: selector,
    }
  );
};
export const usePaymentDetailsOfCompany = (hasFinanceConfigAccess) => {
  const { teamId } = useSelectedTeam();

  return useQuery(
    queryKeys["paymentDetailsOfCompany"](teamId),
    fetchWithError,
    {
      enabled: !!hasFinanceConfigAccess,
      select: selector,
    }
  );
};

export const useGetPaymentGatewayList = () => {
  const mutationResult = useMutation(fetchApi);
  return mutationResult;
};

export const useGetMeterValues = (
  queryKey,
  { showMeterValues, transactionId, connectorId } = {}
) => {
  const { teamId } = useSelectedTeam();
  return useQuery(
    queryKeys[queryKey](teamId, transactionId, connectorId),
    fetchWithError,
    {
      enabled: showMeterValues && !is.empty(teamId) && !is.empty(transactionId),
      select: selector,
    }
  );
};
export const useMutate = (queryKey, { id, onCustomError } = {}, reset = true) => {
  const queryClient = useQueryClient();
  const { teamId } = useSelectedTeam();
  const dispatch = useDispatch();
  const mutationResult = useMutation(fetchApi, {
    onSuccess: (data) => {
      if (queryKey !== "ChargerVisibility") {
        // Handle special cases where query key doesn't expect id parameter
        if (queryKey === "tags") {
          // Invalidate all queries that start with "getTags" to ensure tag list refreshes
          queryClient.invalidateQueries({ queryKey: ["getTags"] });
          // Also invalidate the specific tag detail query if we have an id
          if (id) {
            queryClient.invalidateQueries(queryKeys.tag(teamId, id));
          }
        } else {
          queryClient.invalidateQueries(queryKeys[queryKey](teamId, id));
        }
      }
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: data?.message,
          severity: "success",
        })
      );
    },
    onError: (error) => {
      if (onCustomError) {
        onCustomError(error);
      } else {
        dispatch(
          alertSlice.actions.setAlert({
            open: true,
            message: error?.response?.data?.message ?? error?.message,
            severity: "error",
          })
        );
      }
    },
    onSettled: () => {
      reset && mutationResult.reset();
    },
  });
  return mutationResult;
};

export const useGetPaymentGateWay = () => {
  return useQuery(queryKeys.getPaymentGateway(), fetchWithError);
};

export const useGetPayoutList = (id) => {
  return useQuery(queryKeys.payoutList(id), fetchWithError);
};

export const useCustomer = (id) => {
  return useQuery(queryKeys.cmCustomerById(id), fetchWithError);
};

export const useFetch = () => {
  const fetchData = async ({
    url,
    method = "GET",
    body = null,
    headers = {},
  }) => {
    try {
      // Get the token from localStorage
      const token = Token.get("_flst");

      // Add the Authorization header if the token exists
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...headers, // Spread custom headers passed into fetchData
        },
        body: body ? JSON.stringify(body) : null,
      });

      // Handle the response
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json(); // Parse the response data
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error; // Rethrow error to handle it in the component
    }
  };

  return { fetchData };
};
